import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Dropdown,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  type MenuProps,
} from 'antd';
import {
  ApartmentOutlined,
  DoubleRightOutlined,
  DownOutlined,
  MoreOutlined,
  PlusOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import PageHeader from '../../components/PageHeader';
import PageHelp from '../../components/PageHelp';
import LookupSelect from '../../components/LookupSelect';
import {
  Asset,
  Organization,
  UnifiedSim,
  User,
  createAsset,
  createOrganization,
  deleteAsset,
  deleteOrganization,
  getAssets,
  getOrganizations,
  getSims,
  getUsers,
  moveAssetToOrganization,
  updateAsset,
  updateOrganization,
} from '../../services/adminService';
import { useAuth } from '../../auth/AuthContext';

const { Text } = Typography;

// Each row in the Fleet table is either an Organization or an Asset. We tag
// the kind so renderers and action handlers don't have to guess. Org and asset
// ids can both auto-increment from 1, so the rowKey is composed as
// `${kind}-${id}`.
type FleetRow =
  | {
      kind: 'org';
      id: number;
      org: Organization;
      childOrgCount: number;
      assetCount: number;
      children?: FleetRow[];
    }
  | {
      kind: 'asset';
      id: number;
      asset: Asset;
    };

const ORG_TYPE_TAG_COLOR: Record<string, string> = {
  platform: 'magenta',
  reseller: 'orange',
  customer: 'blue',
};

const ORG_TYPE_LABEL: Record<string, string> = {
  platform: 'Platform',
  reseller: 'Reseller',
  customer: 'Customer',
};

// What sub-org type makes the most sense under each parent type? Used as the
// default; the user can override in the modal.
const DEFAULT_SUB_ORG_TYPE: Record<string, string> = {
  platform: 'reseller',
  reseller: 'customer',
  customer: 'customer',
};

const OrgsAndAssets: React.FC = () => {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [sims, setSims] = useState<UnifiedSim[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const canManageOrgs = role === 'super_admin' || role === 'reseller_admin';
  const canManageAssets =
    role === 'super_admin' || role === 'reseller_admin' || role === 'customer_admin';

  // ──────────────────── Modals ────────────────────
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [orgModalParent, setOrgModalParent] = useState<Organization | null>(null);
  const [orgModalEditing, setOrgModalEditing] = useState<Organization | null>(null);
  const [orgForm] = Form.useForm();
  const [orgSubmitting, setOrgSubmitting] = useState(false);

  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetModalOrg, setAssetModalOrg] = useState<Organization | null>(null);
  const [assetModalEditing, setAssetModalEditing] = useState<Asset | null>(null);
  const [assetModalMode, setAssetModalMode] = useState<'create' | 'existing'>('create');
  const [moveAssetId, setMoveAssetId] = useState<number | null>(null);
  const [assetForm] = Form.useForm();
  const [assetSubmitting, setAssetSubmitting] = useState(false);

  // ──────────────────── Data fetch ────────────────────
  const fetchAll = async () => {
    setLoading(true);
    try {
      // Users + SIMs fail-soft: if a viewer's role can't list one of them,
      // we still render orgs + assets without crashing the page.
      const [orgs, as, us, sm] = await Promise.all([
        getOrganizations(),
        getAssets(),
        getUsers().catch(() => [] as User[]),
        getSims().catch(() => [] as UnifiedSim[]),
      ]);
      setOrganizations(orgs);
      setAssets(as);
      setUsers(us);
      setSims(sm);
    } catch (err: any) {
      message.error(err.message || 'Failed to load fleet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ──────────────────── Hierarchy ────────────────────
  // Build `parent_org_id → child orgs` and `organization_id → assets` maps,
  // then walk from each top-level (parent_org_id IS NULL) org to assemble
  // a nested rows structure that Ant Table renders as expandable rows.
  const { rows, allOrgKeys } = useMemo(() => {
    const byParent = new Map<number | null, Organization[]>();
    for (const o of organizations) {
      if (!o.is_active) continue;
      const arr = byParent.get(o.parent_org_id) || [];
      arr.push(o);
      byParent.set(o.parent_org_id, arr);
    }
    const assetsByOrg = new Map<number, Asset[]>();
    for (const a of assets) {
      if (!a.is_active) continue;
      const arr = assetsByOrg.get(a.organization_id) || [];
      arr.push(a);
      assetsByOrg.set(a.organization_id, arr);
    }
    const orgKeys: React.Key[] = [];

    const buildOrgRow = (org: Organization): FleetRow => {
      const childOrgs = (byParent.get(org.id) || []).sort((a, b) => a.name.localeCompare(b.name));
      const orgAssets = (assetsByOrg.get(org.id) || []).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      const children: FleetRow[] = [
        ...childOrgs.map(buildOrgRow),
        ...orgAssets.map<FleetRow>(a => ({ kind: 'asset', id: a.id, asset: a })),
      ];

      const row: FleetRow = {
        kind: 'org',
        id: org.id,
        org,
        childOrgCount: childOrgs.length,
        assetCount: orgAssets.length,
        children: children.length > 0 ? children : undefined,
      };
      orgKeys.push(rowKey(row));
      return row;
    };

    // An org is a "root" from THIS viewer's perspective if it has no parent OR
    // its parent isn't in the returned org list (i.e. the parent is outside
    // the viewer's RBAC scope). Without this, a customer_admin or asset_manager
    // whose org has a parent they can't see would render an empty tree.
    const visibleOrgIds = new Set(organizations.filter(o => o.is_active).map(o => o.id));
    const roots = organizations
      .filter(o => o.is_active && (o.parent_org_id === null || !visibleOrgIds.has(o.parent_org_id)))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { rows: roots.map(buildOrgRow), allOrgKeys: orgKeys };
  }, [organizations, assets]);

  // Expand all org rows on every data refresh
  useEffect(() => {
    setExpandedRowKeys(allOrgKeys);
  }, [allOrgKeys]);

  function rowKey(r: FleetRow): string {
    return `${r.kind}-${r.id}`;
  }

  // Walk down the tree from a row and return every row key beneath it (sub-orgs
  // + their assets, recursively). Used by the per-row "expand all below" /
  // "collapse all below" actions.
  function collectDescendantKeys(r: FleetRow): string[] {
    if (r.kind === 'asset' || !r.children) return [];
    const keys: string[] = [];
    for (const child of r.children) {
      keys.push(rowKey(child));
      if (child.kind === 'org') keys.push(...collectDescendantKeys(child));
    }
    return keys;
  }

  function expandAllBelow(r: FleetRow) {
    const toAdd = [rowKey(r), ...collectDescendantKeys(r)];
    setExpandedRowKeys(prev => Array.from(new Set([...prev, ...toAdd])));
  }

  function collapseAllBelow(r: FleetRow) {
    const toRemove = new Set(collectDescendantKeys(r));
    setExpandedRowKeys(prev => prev.filter(k => !toRemove.has(String(k))));
  }

  // ──────────────────── Roll-up counts ────────────────────
  // Pre-compute counts of users and SIMs scoped to each org and asset so the
  // Contents column renders without scanning per row.
  const counts = useMemo(() => {
    const usersByOrg = new Map<number, number>();
    for (const u of users) {
      if (!u.is_active) continue;
      usersByOrg.set(u.organization_id, (usersByOrg.get(u.organization_id) || 0) + 1);
    }
    const simsByOrg = new Map<number, number>();
    const simsByAsset = new Map<number, number>();
    for (const s of sims) {
      if (!s.allocation) continue;
      simsByOrg.set(
        s.allocation.organizationId,
        (simsByOrg.get(s.allocation.organizationId) || 0) + 1
      );
      if (s.allocation.assetId) {
        simsByAsset.set(s.allocation.assetId, (simsByAsset.get(s.allocation.assetId) || 0) + 1);
      }
    }
    return { usersByOrg, simsByOrg, simsByAsset };
  }, [users, sims]);

  // ──────────────────── Org handlers ────────────────────
  const openCreateOrg = (parent: Organization | null) => {
    setOrgModalParent(parent);
    setOrgModalEditing(null);
    orgForm.resetFields();
    const defaultType = parent ? DEFAULT_SUB_ORG_TYPE[parent.type] || 'customer' : 'customer';
    orgForm.setFieldsValue({ type: defaultType, parent_org_id: parent?.id ?? null });
    setOrgModalOpen(true);
  };

  const openEditOrg = (org: Organization) => {
    setOrgModalParent(null);
    setOrgModalEditing(org);
    orgForm.resetFields();
    orgForm.setFieldsValue({ name: org.name, type: org.type, parent_org_id: org.parent_org_id });
    setOrgModalOpen(true);
  };

  const handleOrgSubmit = async (values: any) => {
    setOrgSubmitting(true);
    try {
      if (orgModalEditing) {
        await updateOrganization(orgModalEditing.id, {
          name: values.name,
          type: values.type,
          parent_org_id: values.parent_org_id ?? null,
        });
        message.success('Organization updated');
      } else {
        await createOrganization({
          name: values.name,
          type: values.type,
          parent_org_id: values.parent_org_id || undefined,
        });
        message.success('Organization created');
      }
      setOrgModalOpen(false);
      fetchAll();
    } catch (err: any) {
      message.error(err.message || 'Failed to save organization');
    } finally {
      setOrgSubmitting(false);
    }
  };

  const handleDeleteOrg = async (id: number) => {
    try {
      await deleteOrganization(id);
      message.success('Organization deactivated');
      fetchAll();
    } catch (err: any) {
      message.error(err.message || 'Failed to deactivate org');
    }
  };

  // ──────────────────── Asset handlers ────────────────────
  const openCreateAsset = (org: Organization) => {
    setAssetModalOrg(org);
    setAssetModalEditing(null);
    setAssetModalMode('create');
    setMoveAssetId(null);
    assetForm.resetFields();
    // Default to yacht — most common case. User can change.
    assetForm.setFieldsValue({ asset_type: 'yacht' });
    setAssetModalOpen(true);
  };

  const openEditAsset = (a: Asset) => {
    setAssetModalOrg(null);
    setAssetModalEditing(a);
    setAssetModalMode('create'); // edit form path
    assetForm.resetFields();
    assetForm.setFieldsValue({ name: a.name, asset_type: a.asset_type || 'yacht' });
    setAssetModalOpen(true);
  };

  // Assets in OTHER orgs that could be moved INTO assetModalOrg
  const movableAssets = useMemo(() => {
    if (!assetModalOrg) return [];
    return assets.filter(a => a.is_active && a.organization_id !== assetModalOrg.id);
  }, [assets, assetModalOrg]);

  const handleMoveExistingAsset = async () => {
    if (!moveAssetId || !assetModalOrg) return;
    setAssetSubmitting(true);
    try {
      const result = await moveAssetToOrganization(moveAssetId, assetModalOrg.id);
      message.success(
        `Asset moved (${result.moved_sims} SIM(s), ${result.moved_users} user(s) also moved)`
      );
      setAssetModalOpen(false);
      fetchAll();
    } catch (err: any) {
      message.error(err.message || 'Failed to move asset');
    } finally {
      setAssetSubmitting(false);
    }
  };

  const handleAssetSubmit = async (values: any) => {
    setAssetSubmitting(true);
    try {
      if (assetModalEditing) {
        await updateAsset(assetModalEditing.id, {
          name: values.name,
          asset_type: values.asset_type,
        });
        message.success('Asset updated');
      } else if (assetModalOrg) {
        await createAsset({
          name: values.name,
          organizationId: assetModalOrg.id,
          asset_type: values.asset_type || 'yacht',
        });
        message.success('Asset created');
      }
      setAssetModalOpen(false);
      fetchAll();
    } catch (err: any) {
      message.error(err.message || 'Failed to save asset');
    } finally {
      setAssetSubmitting(false);
    }
  };

  const handleDeleteAsset = async (id: number) => {
    try {
      await deleteAsset(id);
      message.success('Asset deleted');
      fetchAll();
    } catch (err: any) {
      message.error(err.message || 'Failed to delete asset');
    }
  };

  // ──────────────────── Columns ────────────────────
  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: any, r: FleetRow) => {
        if (r.kind === 'org') {
          return (
            <Space size="small" align="center">
              <ApartmentOutlined style={{ color: '#8c8c8c' }} />
              <Link to={`/admin/organizations/${r.org.id}`} style={{ fontWeight: 500 }}>
                {r.org.name}
              </Link>
            </Space>
          );
        }
        return (
          <Space size="small" align="center">
            <RocketOutlined style={{ color: '#1677ff' }} />
            <Link to={`/admin/assets/${r.asset.id}`}>{r.asset.name}</Link>
          </Space>
        );
      },
    },
    {
      title: 'Type',
      key: 'type',
      width: 140,
      render: (_: any, r: FleetRow) => {
        if (r.kind === 'org') {
          return (
            <Tag color={ORG_TYPE_TAG_COLOR[r.org.type] || 'default'}>
              {ORG_TYPE_LABEL[r.org.type] || r.org.type}
            </Tag>
          );
        }
        return <Tag>{r.asset.asset_type || 'asset'}</Tag>;
      },
    },
    {
      title: 'Contents',
      key: 'summary',
      width: 300,
      render: (_: any, r: FleetRow) => {
        if (r.kind === 'org') {
          const bits: string[] = [];
          if (r.childOrgCount > 0)
            bits.push(`${r.childOrgCount} sub-org${r.childOrgCount === 1 ? '' : 's'}`);
          if (r.assetCount > 0) bits.push(`${r.assetCount} asset${r.assetCount === 1 ? '' : 's'}`);
          const userCount = counts.usersByOrg.get(r.org.id) || 0;
          const simCount = counts.simsByOrg.get(r.org.id) || 0;
          if (userCount > 0) bits.push(`${userCount} user${userCount === 1 ? '' : 's'}`);
          if (simCount > 0) bits.push(`${simCount} SIM${simCount === 1 ? '' : 's'}`);
          return bits.length > 0 ? (
            <Text type="secondary">{bits.join(' · ')}</Text>
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic' }}>
              empty
            </Text>
          );
        }
        // Asset row: SIM count assigned to this asset
        const simCount = counts.simsByAsset.get(r.asset.id) || 0;
        return simCount > 0 ? (
          <Text type="secondary">
            {simCount} SIM{simCount === 1 ? '' : 's'}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontStyle: 'italic' }}>
            no SIMs
          </Text>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'right' as const,
      render: (_: any, r: FleetRow) => {
        const safe: NonNullable<MenuProps['items']> = [];
        const danger: NonNullable<MenuProps['items']> = [];

        if (r.kind === 'org') {
          // Expand/collapse all below this org — only meaningful if this org has
          // sub-org descendants (assets are leaves and don't expand further).
          const descendantKeys = collectDescendantKeys(r);
          const expandableDescendants = descendantKeys.filter(k => k.startsWith('org-'));
          if (expandableDescendants.length > 0) {
            const expandedSet = new Set(expandedRowKeys.map(String));
            const anyExpanded = expandableDescendants.some(k => expandedSet.has(k));
            const allExpanded = expandableDescendants.every(k => expandedSet.has(k));
            if (!allExpanded) {
              safe.push({
                key: 'expand-all',
                label: 'Expand all below',
                icon: <DownOutlined />,
                onClick: () => expandAllBelow(r),
              });
            }
            if (anyExpanded) {
              safe.push({
                key: 'collapse-all',
                label: 'Collapse all below',
                icon: <DoubleRightOutlined />,
                onClick: () => collapseAllBelow(r),
              });
            }
          }
          if (canManageAssets && r.org.type === 'customer') {
            safe.push({
              key: 'add-asset',
              label: 'Add asset',
              icon: <RocketOutlined />,
              onClick: () => openCreateAsset(r.org),
            });
          }
          if (canManageOrgs) {
            safe.push({
              key: 'add-sub-org',
              label: 'Add sub-organization',
              icon: <PlusOutlined />,
              onClick: () => openCreateOrg(r.org),
            });
            safe.push({
              key: 'edit-org',
              label: 'Edit organization',
              onClick: () => openEditOrg(r.org),
            });
          }
          if (canManageOrgs && r.org.parent_org_id !== null) {
            danger.push({
              key: 'deactivate-org',
              label: 'Deactivate organization',
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: `Deactivate "${r.org.name}"?`,
                  content:
                    'Linked assets and users keep their references; this only hides the org from views.',
                  okText: 'Deactivate',
                  okButtonProps: { danger: true },
                  onOk: () => handleDeleteOrg(r.org.id),
                });
              },
            });
          }
        } else {
          // Asset row
          safe.push({
            key: 'open-asset',
            label: 'Open detail page',
            onClick: () => navigate(`/admin/assets/${r.asset.id}`),
          });
          if (canManageAssets) {
            safe.push({
              key: 'edit-asset',
              label: 'Edit asset',
              onClick: () => openEditAsset(r.asset),
            });
            danger.push({
              key: 'delete-asset',
              label: 'Delete asset',
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: `Delete asset "${r.asset.name}"?`,
                  content: 'Deactivates the asset. SIM allocations stay and can be re-assigned.',
                  okText: 'Delete',
                  okButtonProps: { danger: true },
                  onOk: () => handleDeleteAsset(r.asset.id),
                });
              },
            });
          }
        }

        const items: MenuProps['items'] = [
          ...safe,
          ...(safe.length > 0 && danger.length > 0 ? [{ type: 'divider' as const }] : []),
          ...danger,
        ];

        if (items.length === 0) {
          return <span style={{ color: '#bbb' }}>—</span>;
        }

        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              aria-label={`Actions for ${r.kind === 'org' ? r.org.name : r.asset.name}`}
            />
          </Dropdown>
        );
      },
    },
  ];

  // ──────────────────── Render ────────────────────
  return (
    <div className="page-wrapper" style={{ padding: 24 }}>
      <PageHeader
        title="Org & Assets"
        actions={
          <Space>
            {canManageOrgs && (
              <Button icon={<PlusOutlined />} onClick={() => openCreateOrg(null)}>
                New Organization
              </Button>
            )}
            <PageHelp
              title="Org & Assets Help"
              sections={[
                {
                  title: 'What this view shows',
                  content:
                    'A combined view of your organization hierarchy and the physical assets (yachts, vehicles, etc.) inside each org. Top-level entries are platform / reseller orgs; expand them to see customer orgs and the assets those customers operate.',
                },
                {
                  title: 'Adding things',
                  content:
                    'Click "Add Asset" on any org row to create a new asset under that organization. The modal lets you pick the asset type (yacht, vehicle, etc.).\n\nNote: yacht-type assets can only belong to customer-type organizations. The backend rejects yachts placed under platform or reseller orgs.\n\nUse "Sub-Org" to create a child organization. The default type is suggested based on the parent (platform → reseller, reseller → customer) but you can override it.',
                },
                {
                  title: 'Editing',
                  content:
                    'Click a name to open the org or asset detail page. Use the pencil icon on a row for quick inline edits.\n\nTo move an asset to a different organization, use the "Change…" button on the asset detail page.',
                },
              ]}
            />
          </Space>
        }
      />
      <Card>
        <Table<FleetRow>
          dataSource={rows}
          columns={columns}
          loading={loading}
          rowKey={rowKey}
          pagination={false}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: keys => setExpandedRowKeys([...keys]),
            rowExpandable: r => r.kind === 'org' && !!r.children?.length,
            indentSize: 24,
          }}
          locale={{ emptyText: 'No organizations to display.' }}
        />
      </Card>

      {/* Org create / edit modal */}
      <Modal
        title={
          orgModalEditing
            ? `Edit organization: ${orgModalEditing.name}`
            : orgModalParent
              ? `Add sub-organization under ${orgModalParent.name}`
              : 'New top-level organization'
        }
        open={orgModalOpen}
        onCancel={() => setOrgModalOpen(false)}
        onOk={() => orgForm.submit()}
        confirmLoading={orgSubmitting}
        okText={orgModalEditing ? 'Save' : 'Create'}
      >
        <Form form={orgForm} layout="vertical" onFinish={handleOrgSubmit}>
          {orgModalParent && (
            <p style={{ color: '#666', marginTop: 0 }}>
              Will be created under <strong>{orgModalParent.name}</strong> (
              <Tag color={ORG_TYPE_TAG_COLOR[orgModalParent.type]}>{orgModalParent.type}</Tag>).
            </p>
          )}
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
            <Input placeholder="e.g. Polaris Operations Ltd" autoFocus />
          </Form.Item>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Select a type' }]}
            tooltip="platform = MIMO root (one only); reseller = intermediary distributor; customer = end operator (the right choice for a yacht owner)."
          >
            <Select placeholder="Type">
              <Select.Option value="customer">Customer</Select.Option>
              <Select.Option value="reseller">Reseller</Select.Option>
              <Select.Option value="platform">Platform</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="parent_org_id"
            label="Parent organization"
            tooltip="Leave empty for a top-level org."
          >
            <Select
              placeholder="None (top-level)"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {organizations
                .filter(o => o.is_active && (!orgModalEditing || o.id !== orgModalEditing.id))
                .map(o => (
                  <Select.Option key={o.id} value={o.id}>
                    {o.name} ({o.type})
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Asset create / edit / move modal */}
      <Modal
        title={
          assetModalEditing
            ? `Edit asset: ${assetModalEditing.name}`
            : `Add asset to ${assetModalOrg?.name ?? ''}`
        }
        open={assetModalOpen}
        onCancel={() => setAssetModalOpen(false)}
        onOk={() => {
          if (assetModalMode === 'existing') {
            handleMoveExistingAsset();
          } else {
            assetForm.submit();
          }
        }}
        confirmLoading={assetSubmitting}
        okButtonProps={{
          disabled: assetModalMode === 'existing' && !moveAssetId,
        }}
        okText={
          assetModalEditing
            ? 'Save'
            : assetModalMode === 'existing'
              ? 'Move to this organization'
              : 'Create'
        }
      >
        {!assetModalEditing && assetModalOrg && (
          <Segmented
            block
            value={assetModalMode}
            onChange={v => setAssetModalMode(v as 'create' | 'existing')}
            options={[
              { label: 'Create new', value: 'create' },
              {
                label: `Add existing${movableAssets.length ? ` (${movableAssets.length})` : ''}`,
                value: 'existing',
                disabled: movableAssets.length === 0,
              },
            ]}
            style={{ marginBottom: 16 }}
          />
        )}
        {assetModalMode === 'existing' && assetModalOrg ? (
          <Form layout="vertical">
            <p style={{ color: '#666', marginTop: 0 }}>
              Moving an asset into <strong>{assetModalOrg.name}</strong> (
              <Tag color={ORG_TYPE_TAG_COLOR[assetModalOrg.type]}>{assetModalOrg.type}</Tag>) also
              moves any SIMs allocated to it and any crew users currently linked to it.
            </p>
            <Form.Item label="Asset" required>
              <Select
                showSearch
                placeholder="Select an asset from another organization"
                value={moveAssetId ?? undefined}
                onChange={v => setMoveAssetId(v)}
                optionFilterProp="children"
              >
                {movableAssets.map(a => (
                  <Select.Option key={a.id} value={a.id}>
                    {a.name} ({a.asset_type || 'asset'}) —{' '}
                    {a.org_name || `org ${a.organization_id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        ) : (
          <Form form={assetForm} layout="vertical" onFinish={handleAssetSubmit}>
            {assetModalOrg && (
              <p style={{ color: '#666', marginTop: 0 }}>
                Will be created under <strong>{assetModalOrg.name}</strong> (
                <Tag color={ORG_TYPE_TAG_COLOR[assetModalOrg.type]}>{assetModalOrg.type}</Tag>
                ).
              </p>
            )}
            <Form.Item
              name="name"
              label="Asset name"
              rules={[{ required: true, message: 'Enter a name' }]}
            >
              <Input placeholder="e.g. M/Y Polaris, Tender 1, Service Van" autoFocus />
            </Form.Item>
            <Form.Item
              name="asset_type"
              label="Asset type"
              tooltip="yacht, vehicle, tender, etc. Yachts must be under a customer org; other types are unrestricted."
            >
              <LookupSelect category="asset_type" placeholder="Select or enter type" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default OrgsAndAssets;
